import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Crown, ArrowRight, Building } from 'lucide-react';

const ColumbiaUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Columbia University";

    return (
        <div className="min-h-screen bg-white font-sans text-[#002B7F] selection:bg-[#9BDDFF] selection:text-[#002B7F]">
            {/* Top Navigation */}
            <div className="bg-white border-b border-[#002B7F] py-4 px-6 md:px-12 sticky top-0 z-50">
                <div className="max-w-[1400px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Crown className="text-[#9BDDFF] fill-[#002B7F]" size={36} />
                        <div className="flex flex-col">
                            <span className="font-serif text-3xl text-[#002B7F] leading-none tracking-tight">{schoolName.split(' ')[0].toUpperCase()}</span>
                            <span className="font-serif text-lg text-[#757575] leading-none uppercase tracking-widest">{schoolName.split(' ').slice(1).join(' ') || "UNIVERSITY"}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex items-center gap-8 text-[#002B7F] font-bold text-sm tracking-widest uppercase">
                        <a href="#" className="hover:underline underline-offset-4">About</a>
                        <a href="#" className="hover:underline underline-offset-4">Admissions</a>
                        <a href="#" className="hover:underline underline-offset-4">Academics</a>
                        <a href="#" className="hover:underline underline-offset-4">Research</a>
                        <a href="#" className="hover:underline underline-offset-4">Global</a>
                        <Search className="cursor-pointer ml-4" size={20} />
                    </div>

                     <div className="lg:hidden">
                        <Menu size={28} className="text-[#002B7F]" />
                    </div>
                </div>
            </div>

            {/* Hero Section - City Integration */}
            <header className="relative">
                <div className="h-[600px] relative overflow-hidden">
                    <img 
                        src="https://images.unsplash.com/photo-1548508499-52d3c332924d?q=80&w=2072&auto=format&fit=crop" 
                        alt="Low Library" 
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[#002B7F]/60"></div>
                    <div className="absolute inset-0 flex items-center justify-center text-center p-8">
                        <div>
                             <h1 className="text-5xl md:text-7xl font-serif text-white mb-8 max-w-4xl mx-auto leading-tight">
                                 In the City of New York.
                             </h1>
                             <p className="text-xl text-[#9BDDFF] max-w-2xl mx-auto mb-10 font-light">
                                 A world-class center of learning and research in the heart of a global capital.
                             </p>
                             <Button className="bg-white text-[#002B7F] hover:bg-[#9BDDFF] font-bold uppercase tracking-widest px-8 h-14 rounded-sm text-sm border border-transparent hover:border-[#002B7F] transition-all">
                                 Visit {schoolName.split(' ')[0]}
                             </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* News - Clean & Text Heavy */}
            <section className="py-20 px-6 md:px-12 max-w-[1200px] mx-auto">
                <div className="border-t-4 border-[#9BDDFF] pt-4 mb-12 inline-block">
                    <h2 className="text-3xl font-serif text-[#002B7F]">{schoolName.split(' ')[0]} News</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                     <div className="col-span-1 lg:col-span-2">
                         <img src="https://images.unsplash.com/photo-1544531586-fde5298cdd40?q=80&w=2070&auto=format&fit=crop" className="w-full aspect-[16/9] object-cover mb-6 rounded-sm" alt="Main News" />
                         <span className="text-[#757575] uppercase text-xs font-bold tracking-widest mb-2 block">Climate School</span>
                         <h3 className="text-3xl font-serif text-[#002B7F] mb-4 hover:underline cursor-pointer leading-tight">
                             New research reveals rapid melting of glaciers in the Andes
                         </h3>
                         <p className="text-gray-600 text-lg leading-relaxed">
                             Scientists warn that urgent action is needed to preserve water resources for millions of people.
                         </p>
                     </div>

                     <div className="flex flex-col gap-10">
                         {[
                             `${schoolName.split(' ')[0]} engineers develop AI to detect early signs of Alzheimer's`,
                             `Pulitzer Prize winners announced for 2025: ${schoolName.split(' ')[0]} alumni recognized`,
                             "President delivers annual commencement address on Low Steps",
                             "New arts initiative brings Harlem artists to campus"
                         ].map((title, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <h4 className="text-xl font-serif text-[#002B7F] group-hover:underline leading-snug mb-2">{title}</h4>
                                 <div className="flex items-center gap-2 text-[#757575] text-xs font-bold uppercase tracking-widest group-hover:text-[#9BDDFF] transition-colors">
                                     Read Story <ArrowRight size={12} />
                                 </div>
                             </div>
                         ))}
                     </div>
                </div>
            </section>

            {/* Footer - Columbia Blue */}
            <footer className="bg-[#002B7F] text-white py-16 px-6 md:px-12">
                <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
                     <div>
                         <Crown className="text-[#9BDDFF] fill-white mb-6" size={48} />
                         <p className="text-[#9BDDFF] font-serif text-2xl mb-4">{schoolName}</p>
                         <p className="text-sm text-gray-300">
                             116th and Broadway <br/>
                             New York, NY 10027 <br/>
                             212-854-1754
                         </p>
                     </div>

                     <div className="md:col-span-3 flex flex-wrap gap-12 text-sm font-bold opacity-90">
                         <div>
                             <h4 className="text-[#9BDDFF] uppercase tracking-widest mb-4 text-xs">Schools</h4>
                             <ul className="space-y-2">
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Columbia College</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Engineering</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">General Studies</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Barnard</a></li>
                             </ul>
                         </div>
                         <div>
                             <h4 className="text-[#9BDDFF] uppercase tracking-widest mb-4 text-xs">Resources</h4>
                             <ul className="space-y-2">
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Libraries</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Directory</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Map</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Athletics</a></li>
                             </ul>
                         </div>
                         <div>
                             <h4 className="text-[#9BDDFF] uppercase tracking-widest mb-4 text-xs">Social</h4>
                             <ul className="space-y-2">
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Facebook</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Twitter</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">Instagram</a></li>
                                 <li><a href="#" className="hover:text-[#9BDDFF]">YouTube</a></li>
                             </ul>
                         </div>
                     </div>
                </div>
                <div className="max-w-[1400px] mx-auto mt-12 border-t border-[#1a4497] pt-8 text-center text-xs text-[#9BDDFF]/60">
                    © 2026 {schoolName}
                </div>
            </footer>
        </div>
    );
};

export default ColumbiaUniversity;
